export const DAILY_NOTE_NOT_STORED = 'Not stored';

export interface DailyNoteGoal {
  goalId?: string;
  description: string;
  accuracy: string;
  cueing: string;
  evidence: string;
}

export interface DailyNoteSession {
  dateOfService: string;
  timeIn: string;
  timeOut: string;
  durationMinutes: string;
  serviceCode: string;
  placeOfService: string;
  clientsTreatedTogether: string;
}

export interface DailyTreatmentNoteContent {
  documentId: string;
  narrative: string;
  goals: DailyNoteGoal[];
  observations: string[];
  session: DailyNoteSession;
}

function text(value: unknown): string {
  if (value === null || value === undefined) return '';
  return String(value).trim();
}

function present(value: unknown): string {
  const raw = text(value);
  return raw || DAILY_NOTE_NOT_STORED;
}

function explicitOrFallback(section: Record<string, unknown>, key: string, ...fallbacks: unknown[]): string {
  if (Object.prototype.hasOwnProperty.call(section, key)) return present(section[key]);
  for (const value of fallbacks) {
    const raw = text(value);
    if (raw) return raw;
  }
  return DAILY_NOTE_NOT_STORED;
}

function parseObject(value: unknown): Record<string, unknown> {
  if (!value) return {};
  if (typeof value === 'object' && !Array.isArray(value)) return { ...(value as Record<string, unknown>) };
  try {
    const parsed = JSON.parse(String(value));
    if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) return parsed;
  } catch {
    return {};
  }
  return {};
}

function parseArray(value: unknown): any[] {
  if (Array.isArray(value)) return value;
  const raw = text(value);
  if (!raw) return [];
  try {
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function accuracyLabel(value: unknown): string {
  const raw = text(value);
  if (!raw) return DAILY_NOTE_NOT_STORED;
  if (raw.includes('%')) return raw;
  if (/^\d+(\.\d+)?$/.test(raw)) return `${raw}%`;
  return raw;
}

function goalFrom(raw: any): DailyNoteGoal | null {
  if (!raw || typeof raw !== 'object') return null;
  const description = text(raw.description || raw.goal);
  const accuracy = text(raw.accuracy ?? raw.performance_accuracy ?? raw.current_percentage);
  const cueing = text(raw.cueing || raw.cueing_notes || raw.cueing_assistance);
  const evidence = text(raw.evidence || raw.trial_evidence || raw.evidenceNote || raw.notes);
  if (!description && !accuracy && !cueing && !evidence) return null;
  return {
    goalId: text(raw.goal_id || raw.id),
    description: description || DAILY_NOTE_NOT_STORED,
    accuracy: accuracyLabel(accuracy),
    cueing: cueing || DAILY_NOTE_NOT_STORED,
    evidence: evidence || DAILY_NOTE_NOT_STORED
  };
}

function additionalObjectiveText(objective: string): string {
  const marker = 'Additional Objective Notes:';
  const at = objective.indexOf(marker);
  if (at >= 0) return objective.slice(at + marker.length).trim();
  if (objective.startsWith('Targeted Goals Addressed:')) return '';
  return objective;
}

export function mapDailyTreatmentNote(doc: any): DailyTreatmentNoteContent {
  const section = parseObject(doc?.section_data_json);
  const narrative = text(section.session_narrative) || text(doc?.subjective);
  const goals = parseArray(section.targeted_goals).concat(parseArray(doc?.goals_addressed_json))
    .map(goalFrom)
    .filter((goal): goal is DailyNoteGoal => Boolean(goal));
  const seenGoals = new Set<string>();
  const uniqueGoals = goals.filter((goal) => {
    const key = `${goal.description}|${goal.accuracy}|${goal.cueing}|${goal.evidence}`;
    if (seenGoals.has(key)) return false;
    seenGoals.add(key);
    return true;
  });

  const candidates = [
    text(section.clinician_evidence),
    text(section.additional_observations),
    additionalObjectiveText(text(doc?.objective)),
    text(doc?.subjective) !== narrative ? text(doc?.subjective) : '',
    text(doc?.assessment),
    text(doc?.plan),
    text(doc?.recommendations),
    text(doc?.medical_necessity)
  ];
  const seen = new Set<string>();
  const observations = candidates.filter((value) => {
    if (!value || value === narrative || seen.has(value)) return false;
    seen.add(value);
    return true;
  });

  return {
    documentId: present(doc?.id),
    narrative: narrative || DAILY_NOTE_NOT_STORED,
    goals: uniqueGoals,
    observations,
    session: {
      dateOfService: explicitOrFallback(section, 'dos', doc?.dos, doc?.linked_service_dos, doc?.document_date),
      timeIn: explicitOrFallback(section, 'time_in', doc?.start_time, doc?.linked_start_time),
      timeOut: explicitOrFallback(section, 'time_out', doc?.end_time, doc?.linked_end_time),
      durationMinutes: explicitOrFallback(section, 'duration_minutes', doc?.actual_duration_minutes, doc?.duration_minutes, doc?.linked_actual_duration_minutes, doc?.linked_duration_minutes),
      serviceCode: explicitOrFallback(section, 'service_code', doc?.service_code, doc?.linked_service_code),
      placeOfService: explicitOrFallback(section, 'place_of_service', doc?.place_of_service, doc?.linked_place_of_service),
      clientsTreatedTogether: explicitOrFallback(section, 'clients_treated_together', doc?.clients_treated_together, doc?.linked_clients_treated_together)
    }
  };
}

export function formatDailyNoteGoals(goals: DailyNoteGoal[]): string {
  if (!goals.length) return DAILY_NOTE_NOT_STORED;
  return goals.map((goal, index) => (
    `Goal ${index + 1}: ${goal.description}. Accuracy: ${goal.accuracy}. Cueing: ${goal.cueing}. Trial evidence: ${goal.evidence}.`
  )).join('\n');
}

export function dailyNoteDraftUpdate(doc: any, edit: { narrative: string; goals: DailyNoteGoal[]; observations: string }) {
  const section = parseObject(doc?.section_data_json);
  const narrative = text(edit.narrative);
  const observations = text(edit.observations);
  section.session_narrative = narrative;
  section.additional_observations = observations;
  section.clinician_evidence = '';
  const goals = (edit.goals || []).filter((goal) => (
    text(goal.description) || text(goal.accuracy) || text(goal.cueing) || text(goal.evidence)
  ));
  const goalLines = goals.map((goal, index) => {
    const accuracy = text(goal.accuracy) || DAILY_NOTE_NOT_STORED;
    const cueing = text(goal.cueing) || DAILY_NOTE_NOT_STORED;
    const evidence = text(goal.evidence);
    return `${index + 1}. ${text(goal.description) || DAILY_NOTE_NOT_STORED} — Accuracy: ${accuracy}, Cueing: ${cueing}${evidence ? ` [Trial evidence: ${evidence}]` : ''}`;
  });
  return {
    subjective: narrative,
    objective: goalLines.length ? `Targeted Goals Addressed:\n${goalLines.join('\n')}` : '',
    assessment: '',
    plan: '',
    recommendations: '',
    medical_necessity: '',
    goals_addressed_json: JSON.stringify(goals.map((goal) => ({
      goal_id: text(goal.goalId),
      description: text(goal.description),
      accuracy: text(goal.accuracy),
      cueing: text(goal.cueing),
      evidence: text(goal.evidence)
    }))),
    section_data_json: JSON.stringify(section)
  };
}

function sourceOf(doc: any): string {
  const column = text(doc?.source).toUpperCase();
  const meta = parseObject(doc?.metadata_json);
  return column || text(meta.source).toUpperCase();
}

/** Uploaded originals and externally completed signed files stay as stored bytes. Bloom-authored daily notes render from stored fields. */
export function preserveStoredDailyNoteFile(doc: any): boolean {
  if (text(doc?.doc_type).toUpperCase() !== 'SOAP') return true;
  const source = sourceOf(doc);
  if (source === 'MANUAL_UPLOAD' || source === 'INBOUND_FAX' || source === 'INCOMING_FAX' || source === 'EXTERNAL') {
    return true;
  }
  const meta = parseObject(doc?.metadata_json);
  if (source === 'POINT_OF_CARE' || meta.is_point_of_care === true) return false;
  const envelope = text(doc?.esign_envelope_id);
  const esignStatus = text(doc?.esign_status).toUpperCase();
  if (envelope && (esignStatus === 'COMPLETED' || esignStatus === 'SIGNED')) return true;
  return false;
}
